import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavScroll } from '../hooks/useNavScroll';
import { useActiveSection } from '../hooks/useActiveSection';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kober-energy/artifacts/vys7c9cr_3.png";

// ── Style helpers (extracted to lower cyclomatic complexity of Navbar fn) ──

function getNavbarStyles(scrolled) {
  return {
    background:           scrolled ? 'rgba(5,5,5,0.94)' : 'transparent',
    backdropFilter:       scrolled ? 'blur(20px) saturate(1.4)' : 'blur(4px)',
    WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'blur(4px)',
    borderBottom:         scrolled ? '1px solid rgba(212,160,23,0.18)' : '1px solid transparent',
    transition:           'background 0.4s ease, border-color 0.4s ease',
  };
}

function getInnerStyles(scrolled) {
  return { height: scrolled ? '68px' : '176px', transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1)' };
}

function getLogoStyles(scrolled) {
  return { height: scrolled ? '44px' : '160px', width: 'auto', transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1)' };
}

const NAV_LINKS = [
  { key: 'about',          id: 'about' },
  { key: 'renewable',      id: 'renewable' },
  { key: 'financing',      id: 'financing' },
  { key: 'bess',           id: 'bess' },
  { key: 'projects',       id: 'projects' },
  { key: 'holyGrail',      id: null, route: '/holy-grail' },
  { key: 'ciEnergy',       id: null, route: '/ci-energy' },
  { key: 'gpTransformers', id: null, route: '/gp-transformers' },
  { key: 'news',           id: 'news' },
];

const MOBILE_VARIANTS = { hidden: { opacity: 0, y: -8 }, visible: { opacity: 1, y: 0 } };
const UNDERLINE_ANIMATE_TRUE  = { scaleX: 1, opacity: 1 };
const UNDERLINE_ANIMATE_FALSE = { scaleX: 0, opacity: 0 };
const UNDERLINE_TRANSITION    = { duration: 0.25, ease: 'easeOut' };
const MOBILE_MENU_TRANSITION  = { duration: 0.2 };

function getIsActive(link, activeSection, currentPath) {
  if (link.route) return currentPath === link.route;
  if (currentPath !== '/') return false;
  return activeSection === link.id;
}

function getNavLinkColor(isActive, isSpecial) {
  if (isActive) return '#D4A017';
  if (isSpecial) return 'rgba(212,160,23,0.65)';
  return 'rgba(255,255,255,0.65)';
}

function NavLink({ link, text, isActive, onClick }) {
  const isSpecial = !!link.route;
  const color = getNavLinkColor(isActive, isSpecial);
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${link.key}`}
      className="relative text-sm tracking-wide py-1 transition-colors duration-200"
      style={{ color, fontWeight: isSpecial ? '500' : '400' }}
    >
      {text[link.key]}
      <motion.span
        className="absolute bottom-0 left-0 right-0 h-px bg-[#D4A017] origin-left"
        initial={false}
        animate={isActive ? UNDERLINE_ANIMATE_TRUE : UNDERLINE_ANIMATE_FALSE}
        transition={UNDERLINE_TRANSITION}
      />
    </button>
  );
}

function MobileMenu({ open, links, text, activeSection, currentPath, onLinkClick, onContact }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-menu"
          variants={MOBILE_VARIANTS}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={MOBILE_MENU_TRANSITION}
          className="lg:hidden border-t shadow-2xl"
          style={{ background: 'rgba(5,5,5,0.97)', backdropFilter: 'blur(20px)', borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="px-6 py-5 flex flex-col gap-1">
            {links.map((link) => (
              <button
                key={link.key}
                onClick={() => onLinkClick(link)}
                className="text-left py-3 transition-colors duration-200 border-b border-white/5 last:border-0"
                style={{ color: getIsActive(link, activeSection, currentPath) ? '#D4A017' : 'rgba(255,255,255,0.75)' }}
              >
                {text[link.key]}
              </button>
            ))}
            <button
              onClick={onContact}
              data-testid="mobile-contact-btn"
              className="mt-4 bg-[#D4A017] text-black font-semibold px-5 py-3 text-sm"
            >
              {text.contact}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar({ text, lang, setLang, onContact }) {
  const [menuOpen, setMenuOpen]       = useState(false);
  const { scrolled, scrollProgress } = useNavScroll();
  const activeSection                = useActiveSection();
  const navigate                     = useNavigate();
  const location                     = useLocation();

  const isHolyGrailPage = location.pathname === '/holy-grail' ||
                          location.pathname === '/ci-energy' ||
                          location.pathname === '/gp-transformers';

  const handleNavClick = (link) => {
    setMenuOpen(false);
    if (link.route) {
      navigate(link.route);
    } else if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: link.id } });
    } else {
      document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
      style={getNavbarStyles(scrolled)}
    >
      {/* Scroll progress bar */}
      <div
        className="absolute top-0 left-0 h-[2px]"
        style={{
          width: `${scrollProgress}%`,
          background: 'linear-gradient(90deg, #D4A017, #F5B814)',
          transition: 'width 0.1s linear',
          boxShadow: '0 0 8px rgba(212,160,23,0.6)',
        }}
      />

      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div
          className="flex items-center justify-between"
          style={getInnerStyles(scrolled)}
        >
          {/* Logo */}
          <button onClick={() => handleNavClick({ id: 'hero' })} className="flex-shrink-0 focus:outline-none">
            <img
              src={LOGO_URL}
              alt="KÖBER Energy Holding"
              style={getLogoStyles(scrolled)}
            />
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.key}
                link={link}
                text={text}
                isActive={getIsActive(link, activeSection, location.pathname)}
                onClick={() => handleNavClick(link)}
              />
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm" data-testid="lang-toggle">
              <button
                data-testid="lang-toggle-en"
                onClick={() => setLang('EN')}
                className={`px-2 py-1 font-medium transition-colors duration-200 ${lang === 'EN' ? 'text-[#D4A017]' : 'text-white/40 hover:text-white/70'}`}
              >EN</button>
              <span className="text-white/20 text-xs">/</span>
              <button
                data-testid="lang-toggle-ro"
                onClick={() => setLang('RO')}
                className={`px-2 py-1 font-medium transition-colors duration-200 ${lang === 'RO' ? 'text-[#D4A017]' : 'text-white/40 hover:text-white/70'}`}
              >RO</button>
            </div>
            <button
              data-testid="navbar-contact-btn"
              onClick={onContact}
              className="hidden sm:block bg-[#D4A017] hover:bg-[#F5B814] text-black text-sm font-semibold px-5 py-2.5 transition-colors duration-200"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {text.contact}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-1"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <MobileMenu
        open={menuOpen}
        links={NAV_LINKS}
        text={text}
        activeSection={activeSection}
        currentPath={location.pathname}
        onLinkClick={handleNavClick}
        onContact={() => { setMenuOpen(false); onContact(); }}
      />
    </nav>
  );
}
